import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { View, Platform } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { TabView, TabBar } from 'react-native-tab-view'
import PropTypes from 'prop-types'

import { Modal, ConfirmationDialog, Txt, Button, KeyboardView } from '../../../../components'
import CapacityForm from './CapacityForm'
import useStyles from './useStyles'
// import TractorsForm from './TractorsForm'
import { addRecurringTractor, updateRecurringTractor } from '../../../../redux/tractors/recurringActions'
import { useTheme } from '../../../../theming/ThemeProvider'
import { usePlatforms } from '../../../../hooks/usePlatforms'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { recurringSelector } from '../../../../redux/tractors/recurringSelector'
import ItineraryForm from './ItineraryForm'
import TractorsForm from './TractorsForm'
import moment from 'moment'

const validationSchema = Yup.object().shape({
  origin: Yup.object().nullable()
    .required('Origin is required'),
  destination: Yup.object().nullable(),
  start_date: Yup.object().shape({
    date: Yup.date().nullable().required('Start date is required')
  }),
  end_date: Yup.object().shape({
    date: Yup.date().nullable().required('End date is required')
  }),
  contact_name: Yup.string().nullable(),
  contact_email: Yup.string().email('Invalid email address').nullable(),
  contact_phone: Yup.string().matches('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$', 'Invalid phone number').nullable(),
  contact_fax: Yup.string().matches('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$', 'Invalid fax number').nullable(),
  tractors_available_monday: Yup.number('field to have an invalid format').nullable(),
  tractors_available_tuesday: Yup.number('field seems to have an invalid format').nullable(),
  tractors_available_wednesday: Yup.number('field seems to have an invalid format').nullable(),
  tractors_available_thursday: Yup.number('field seems to have an invalid format').nullable(),
  tractors_available_friday: Yup.number('field seems to have an invalid format').nullable(),
  tractors_available_saturday: Yup.number('field seems to have an invalid format').nullable(),
  tractors_available_sunday: Yup.number('field seems to have an invalid format').nullable()
})

const initialValues = {
  recurring_tractor_id: '',
  carrier_id: '',
  comments: '',
  contact_email: '',
  contact_fax: '',
  contact_name: '',
  contact_phone: '',
  dispatcher_user_id: '',
  expire_day: '',
  teams_friday: '',
  teams_monday: '',
  teams_saturday: '',
  teams_sunday: '',
  teams_thursday: '',
  teams_tuesday: '',
  teams_wednesday: '',
  tractors_available_friday: '',
  tractors_available_monday: '',
  tractors_available_saturday: '',
  tractors_available_sunday: '',
  tractors_available_thursday: '',
  tractors_available_tuesday: '',
  tractors_available_wednesday: '',
  start_date: {
    date: '',
    offset: 0
  },
  end_date: {
    date: '',
    offset: 0
  },
  equipment_type_id_friday: {
    equipment_code: '',
    description: ''
  },
  equipment_type_id_monday: {
    equipment_code: '',
    description: ''
  },
  equipment_type_id_saturday: {
    equipment_code: '',
    description: ''
  },
  equipment_type_id_sunday: {
    equipment_code: '',
    description: ''
  },
  equipment_type_id_thursday: {
    equipment_code: '',
    description: ''
  },
  equipment_type_id_tuesday: {
    equipment_code: '',
    description: ''
  },
  equipment_type_id_wednesday: {
    equipment_code: '',
    description: ''
  },
  origin: null,
  destination: null
}

const RecurringTractorModal = ({ open, recurringTactorToEdit, onClose }) => {
  const { addLoading, editLoading } = useSelector(recurringSelector)
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(null)
  const [tabsIndex, setTabsIndex] = React.useState(0)
  const [routes] = React.useState([
    { key: 'capacity', title: 'Capacity' },
    { key: 'itinerary', title: 'Itinerary' }
  ])
  const { theme } = useTheme()
  const { isTablet } = usePlatforms()
  const styles = useStyles(theme, isTablet)
  const dispatch = useDispatch()

  const onSubmit = (values) => setConfirmationDialogOpen(values)

  const { setValues, resetForm, ...formik } = useFormik({
    validationSchema,
    initialValues: recurringTactorToEdit || initialValues,
    onSubmit
  })

  useEffect(() => {
    resetForm(recurringTactorToEdit || initialValues)
    setValues(recurringTactorToEdit || initialValues)
  }, [setValues, resetForm, recurringTactorToEdit, open])

  const setHandleSubmit = () => {
    const { handleSubmit, setFieldError, values, setFieldTouched } = formik
    let error = false
    if (values.origin && values.destination && JSON.stringify(values.origin) === JSON.stringify(values.destination)) {
      setFieldError('destination', 'Origin and destination can not be the same', false)
      setFieldTouched('destination', true)
      error = true
    }
    if (values.start_date?.date && values.end_date?.date && moment(values.start_date?.date).isAfter(values.end_date?.date)) {
      setFieldError('end_date.date', 'End date must be later than start date', false)
      setFieldTouched('end_date.date', true)
      error = true
    }
    if (error) return
    handleSubmit()
  }

  const handleConfirmationDialog = useCallback((action) => {
    if (action) {
      if (recurringTactorToEdit) {
        dispatch(updateRecurringTractor(confirmationDialogOpen, onClose))
      } else {
        dispatch(addRecurringTractor(confirmationDialogOpen, onClose))
      }
    }
    setConfirmationDialogOpen(false)
  }, [confirmationDialogOpen, dispatch, onClose, recurringTactorToEdit])

  const smallDevice = useMemo(() => Platform.OS !== 'web' || !isTablet, [isTablet])

  const renderScene = (params, formikParams) => {
    switch (params.route.key) {
      case 'capacity':
        return (
          <CapacityForm
            formikParams={formikParams}
          />
        )
      case 'itinerary':
        return smallDevice ? (
          <TractorsForm
            formikParams={formikParams}
          />
        ) : (
          <ItineraryForm
            formikParams={formikParams}
          />
        )
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Load Entry'
      footer={() => (
        <View style={[styles.buttonsContainer, { flex: smallDevice ? 0 : 1 }]}>
          {!smallDevice && (
            <Button
              title='Cancel'
              onPress={onClose}
              containerStyle={styles.cancelButton}
            />
          )}
          <Button
            title={recurringTactorToEdit ? 'Save' : 'Add'}
            onPress={() => setHandleSubmit()}
            loading={addLoading || editLoading}
            testID='recurringTractorButton'
            accessibilityLabel='recurringTractorButton'
          />
        </View>
      )}
    >
      <View style={{ flex: 1 }}>
        <KeyboardView>
          <TabView
            navigationState={{ index: tabsIndex, routes }}
            renderScene={params => renderScene(params, formik)}
            onIndexChange={setTabsIndex}
            initialLayout={styles.tabsInitialLayout}
            sceneContainerStyle={styles.scene}
            renderTabBar={tabBarProps => (
              <TabBar
                {...tabBarProps}
                style={styles.tabs}
                indicatorStyle={styles.tabsIndicator}
                renderLabel={({ route, focused }) => (
                  <Txt
                    text={route.title}
                    identity='body1Bold'
                    color={focused ? 'AZTEC' : 'SLATE_GRAY'}
                  />
                )}
              />
            )}
          />
        </KeyboardView>

        <ConfirmationDialog
          open={Boolean(confirmationDialogOpen)}
          title={`${recurringTactorToEdit ? 'Edit' : 'Add'} Recurring Tractor`}
          description='Are sure you want to save this recurring tractor?'
          onClose={handleConfirmationDialog}
        />
      </View>
    </Modal>
  )
}

RecurringTractorModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  recurringTactorToEdit: PropTypes.object
}

RecurringTractorModal.defaultProps = {
  open: false,
  onClose: () => {},
  recurringTactorToEdit: null
}

export default RecurringTractorModal
